import { premiumRequestRecovery } from './premium-request-recovery.js';
import { createUuid } from './create-uuid.js';

const MESSAGE_MAX_LENGTH = 2000;

export function premiumRefundRequests(request, messages, checkAccount = () => true, accountId = '') {
    return {
        messageMaxLength: MESSAGE_MAX_LENGTH,
        opened: false,
        loading: false,
        working: false,
        error: '',
        loadErrors: { payments: '', receipts: '' },
        payments: [],
        receipts: [],
        morePayments: false,
        moreReceipts: false,
        selectedPayment: '',
        reason: 'other',
        message: '',
        ...premiumRequestRecovery(accountId, 'refund', payload => payload &&
            typeof payload.payment_id === 'string' && /^\d+$/.test(payload.payment_id) &&
            typeof payload.request_key === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(payload.request_key) &&
            ['first_purchase', 'accidental_renewal', 'statutory', 'other'].includes(payload.reason) &&
            typeof payload.message === 'string' && payload.message.length <= MESSAGE_MAX_LENGTH, messages, 'request_key'),

        restoreRequest(refresh = true) {
            const hadPendingRequest = this.requestToRetry !== null;
            const restored = !refresh || this.restorePending();
            if (restored && hadPendingRequest && !this.requestToRetry) {
                this.selectedPayment = '';
                this.reason = 'other';
                this.message = '';
                return false;
            }
            if (restored && this.requestToRetry) {
                this.selectedPayment = this.requestToRetry.payment_id;
                this.reason = this.requestToRetry.reason;
                this.message = this.requestToRetry.message;
            }
            return restored;
        },

        async open() {
            if (!checkAccount()) return;
            this.opened = true;
            await this.load();
        },

        async load(kind = null) {
            if (!checkAccount() || this.loading || this.working) return;
            this.loading = true;
            this.error = '';
            this.restoreRequest();
            try {
                for (const item of kind ? [kind] : ['payments', 'receipts']) {
                    this.loadErrors[item] = '';
                    try {
                        const endpoint = item === 'payments' ? '/plus-payment' : '/plus-refund-request';
                        const current = this[item];
                        const before = kind && current.length ? `?before_id=${encodeURIComponent(current.at(-1).id)}` : '';
                        const result = await request(endpoint + before, { method: 'GET' });
                        if (!checkAccount()) return;
                        const rows = result[item === 'payments' ? 'payments' : 'requests'];
                        if (!Array.isArray(rows)) throw new Error('Invalid payment history');
                        this[item] = kind ? [...current, ...rows.filter(row => !current.some(existing => existing.id === row.id))] : rows;
                        this[item === 'payments' ? 'morePayments' : 'moreReceipts'] = rows.length === 50;
                    } catch {
                        this.loadErrors[item] = messages.loadError;
                    }
                }
            } finally {
                this.loading = false;
            }
        },

        async submit() {
            if (!checkAccount() || this.working || this.loading) return;
            if (!this.restoreRequest()) return;
            let payload = this.requestToRetry;
            if (!payload) {
                if (!this.payments.some(payment => payment.id === this.selectedPayment)) return;
                if (!['first_purchase', 'accidental_renewal', 'statutory', 'other'].includes(this.reason)) return;
                const requestKey = createUuid();
                if (!requestKey || this.message.length > MESSAGE_MAX_LENGTH) {
                    this.error = messages.loadError;
                    return;
                }
                payload = {
                    payment_id: this.selectedPayment,
                    request_key: requestKey,
                    reason: this.reason,
                    message: this.message,
                };
            }
            const body = this.persistPending(payload);
            if (body === null) return;
            this.restoreRequest(false);
            this.working = true;
            this.error = '';
            try {
                const receipt = await request('/plus-refund-request', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body,
                });
                if (!checkAccount()) return;
                this.receipts = [receipt, ...this.receipts.filter(item => item.id !== receipt.id)];
                if (!this.clearPending(body)) return;
                this.selectedPayment = '';
                this.reason = 'other';
                this.message = '';
                this.restoreRequest();
            } catch (error) {
                if (!checkAccount()) return;
                if ([400, 422].includes(error.status) && this.clearPending(body)) this.restoreRequest(false);
                this.error = messages.loadError;
            } finally {
                this.working = false;
            }
        },

        receiptReason(receipt) {
            return messages.refundReasons?.[receipt.reason] || messages.refundReasons?.other || '';
        },

        decisionAmount(receipt, kind, formatAmount) {
            if (!['approved', 'refunded'].includes(kind) || receipt.decision?.outcome !== 'approved') return '';
            const amount = receipt.decision[`${kind}_amount_cents`];
            const currency = receipt.decision[`${kind}_currency`];
            if (!Number.isSafeInteger(amount) || amount < 0 || typeof currency !== 'string' || !/^[a-z]{3}$/i.test(currency)) return '';
            return formatAmount(amount, currency);
        },

        receiptStatus(receipt) {
            return messages.refundStatuses?.[receipt.status] || messages.refundStatuses?.requires_action || '';
        },
    };
}

export function premiumHistoryProviders(status, currentProvider) {
    const history = Array.isArray(status?.billing_history_store_providers)
        ? status.billing_history_store_providers : [];
    return ['apple', 'google'].filter(provider => provider === currentProvider || history.includes(provider));
}
